import React, {Fragment} from "react";
import {withRouter} from "./Utils";
import "./HalComponent.css";
import config, { getApiUrl } from "./config";
import authService from "./AuthService";

type Mount = {
    friendlyName: string
    type: string
    source: string
    target: string
    description: string
}

type ConfigurationParameter = {
    key: string
    friendlyName: string
    type: string
    constraint: string
    regex: string
    defaultValue: string
    description: string
    ioModes?: Array<string>
}

type InterfaceMode = {
    type: string
    configurationParameters?: Array<ConfigurationParameter>  // set as io.interfaces.<interface>.<key>
}

type Interface = {
    friendlyName: string
    modes: Array<InterfaceMode>
}

type HalComponentDetails = {
    id: number
    imageReference: string
    friendlyName: string
    version: string
    mounts: Array<Mount>
    configurationParameters: Array<ConfigurationParameter>
    interfaces?: Record<string, Interface>
}

type HalComponentProperties = {
    params?: Readonly<any>
}

type HalComponentState = {
    id: number
    details?: HalComponentDetails
}

class HalComponent extends React.Component<HalComponentProperties, HalComponentState> {
    constructor(props: HalComponentProperties) {
        super(props);
        this.state = {
            id: props.params!["halId"] || 0
        }
        this.fetchDetails();
    }

    private fetchDetails() {
        fetch(getApiUrl() + "/v1/component/platform/hal/" + this.state.id, {
            headers: [
                ["Accept", "application/json"],
                ["Authorization", authService.authHeader()]
            ]
        })
            .then(response => response.json() as Promise<HalComponentDetails>)
            .then((response) => {
                this.setState({
                    details: response
                })
            })
            .catch((error) => {
                console.error(error);
            });
    }

    render() {
        return (
            <div className="HalComponent">
                <h2>{this.state.details?.friendlyName || "HAL Component #" + this.state.id}</h2>
                {this.renderDetails()}
            </div>
        );
    }

    private renderDetails() {
        if (!this.state.details) {
            return <div>Loading...</div>
        }

        return <Fragment>
            <h3>Details:</h3>
            <div className="HalComponent-properties">
                <div>Docker image:</div>
                <div className="HalComponent-value">{this.state.details.imageReference}:{this.state.details.version}</div>
            </div>
            <h3>Configuration Parameters:</h3>
            {this.renderConfigurationParameters()}
            <h3>I/O Interfaces:</h3>
            {this.renderInterfaces()}
            <h3>Required mounts:</h3>
            {this.renderMounts()}
        </Fragment>
    }

    private renderConfigurationParameters() {
        return this.state.details!.configurationParameters.map((parameter) => {
            return <div className="HalComponent-configurationParameter" key={parameter.key}>
                <h5><small>{parameter.constraint}</small>&nbsp;{parameter.key}&nbsp;<small>: {parameter.type} = {parameter.defaultValue}</small></h5>
                {parameter.ioModes && parameter.ioModes.length > 0 &&
                    <p><small>I/O modes: {parameter.ioModes.join(", ")}</small></p>}
                <p>{parameter.description}</p>
            </div>
        });
    }

    private renderInterfaces() {
        const interfaces = Object.entries(this.state.details!.interfaces || {});
        if (interfaces.length === 0) {
            return <div>No I/O interfaces declared</div>
        }

        return interfaces.map(([marking, iface]) => {
            return <div className="HalComponent-interface" key={marking}>
                <h5>{marking}&nbsp;<small>{iface.friendlyName}</small></h5>
                <p>{iface.modes.map((mode, index) => index === 0 ? mode.type + " (default)" : mode.type).join(", ")}</p>
                {iface.modes.length > 1 && <p><small>Select with <code>io.interfaces.{marking}</code> in the HAL configuration</small></p>}
                {iface.modes.filter((mode) => mode.configurationParameters?.length).map((mode) =>
                    <p key={mode.type}><small>{mode.type}: {mode.configurationParameters!.map((parameter) =>
                        `io.interfaces.${marking}.${parameter.key}` + (parameter.defaultValue ? ` (default ${parameter.defaultValue})` : "")
                    ).join(", ")}</small></p>)}
            </div>
        });
    }

    private renderMounts() {
        return this.state.details!.mounts.map((mount) => {
            return <div className="HalComponent-mount" key={mount.friendlyName}>
                <h5>{mount.source} <small>TO</small> {mount.target} <small>{mount.type}</small></h5>
                <p>{mount.description}</p>
            </div>
        });
    }
}

const HOCHalComponent = withRouter(HalComponent);
export default HOCHalComponent;